import React from 'react';
import { AutoComplete } from 'primereact/autocomplete';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import type { FleetDriverListDriver } from '../../types/driverTypes';
import { useApiFetch } from '../../api/fetcher';
import { FLEET_DRIVERS_URL } from '../../api/urls';
import { translateApiError } from '../../utils/apiErrorTranslations';
import { NewDriverMiniForm } from './NewDriverMiniForm';
import './VehicleForm.scss';

const normalizeDriverPhone = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    return digits.startsWith('48') ? `+${digits}` : `+48${digits}`;
};

export type CarModel = { id: number; model_name: string };
export type CarMaker = { id: number; maker_name: string; car_models: CarModel[] };

export interface VehicleFormData {
    maker: CarMaker;
    model: CarModel;
    plate: string;
    drivers: FleetDriverListDriver[];
}

export const normalizePlate = (input: string) =>
    (input || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);

export interface VehicleInitial {
    makerId?: number;
    modelId?: number;
    plate?: string;
    drivers?: FleetDriverListDriver[];
}

interface VehicleFormProps {
    makers: CarMaker[];
    allDrivers: FleetDriverListDriver[];
    initial?: VehicleInitial;
    onSubmit: (data: VehicleFormData) => void;
    onCancel?: () => void;
    onDriverCreated?: (driver: FleetDriverListDriver) => void;
    submitLabel?: string;
    saving?: boolean;
    title?: string;
}

export const VehicleForm: React.FC<VehicleFormProps> = ({
    makers,
    allDrivers,
    initial,
    onSubmit,
    onCancel,
    onDriverCreated,
    submitLabel = 'Dodaj pojazd',
    saving = false,
    title,
}) => {
    const apiFetch = useApiFetch();
    const initMaker = initial?.makerId != null
        ? makers.find(m => m.id === initial.makerId) ?? null
        : null;
    const initModel = initMaker?.car_models.find(m => m.id === initial?.modelId) ?? null;

    const [selectedMaker, setSelectedMaker] = React.useState<CarMaker | null>(initMaker);
    const [selectedModel, setSelectedModel] = React.useState<CarModel | null>(initModel);
    const [makerQ, setMakerQ] = React.useState<string>(initMaker?.maker_name ?? '');
    const [modelQ, setModelQ] = React.useState<string>(initModel?.model_name ?? '');
    const [plate, setPlate] = React.useState<string>(initial?.plate ?? '');
    const [selectedDrivers, setSelectedDrivers] = React.useState<FleetDriverListDriver[]>(
        initial?.drivers ?? []
    );

    const [makerS, setMakerS] = React.useState<CarMaker[]>([]);
    const [modelS, setModelS] = React.useState<CarModel[]>([]);
    const [driverS, setDriverS] = React.useState<FleetDriverListDriver[]>([]);

    const [newDriverPhone, setNewDriverPhone] = React.useState('');
    const [newDriverName, setNewDriverName] = React.useState('');
    const [creatingDriver, setCreatingDriver] = React.useState(false);
    const [createDriverError, setCreateDriverError] = React.useState<string | null>(null);

    const makerRef = React.useRef<AutoComplete>(null);
    const modelRef = React.useRef<AutoComplete>(null);
    const driversRef = React.useRef<AutoComplete>(null);

    const handleCreateDriver = async () => {
        if (!newDriverPhone.trim() || !newDriverName.trim() || creatingDriver) return;
        setCreatingDriver(true);
        setCreateDriverError(null);
        try {
            const res = await apiFetch(FLEET_DRIVERS_URL, {
                method: 'POST',
                body: JSON.stringify({
                    phone_number: normalizeDriverPhone(newDriverPhone),
                    first_name: newDriverName.trim(),
                    car_ids: [],
                }),
                credentials: 'include',
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) {
                const phoneMsg = Array.isArray(json?.phone_number) ? json.phone_number[0] : undefined;
                const message = phoneMsg
                    ? translateApiError(phoneMsg, 'Nieprawidłowy numer telefonu.')
                    : json?.detail || 'Nie udało się dodać kierowcy';
                setCreateDriverError(message);
                return;
            }
            const created: FleetDriverListDriver = {
                id: json.id,
                phone_number: json.phone_number,
                first_name: json.first_name ?? newDriverName.trim(),
                last_name: json.last_name ?? null,
                cars: Array.isArray(json.cars) ? json.cars : [],
            };
            setSelectedDrivers(prev => (prev.some(d => d.id === created.id) ? prev : [...prev, created]));
            onDriverCreated?.(created);
            setNewDriverPhone('');
            setNewDriverName('');
        } catch (e) {
            setCreateDriverError('Nie udało się dodać kierowcy');
        } finally {
            setCreatingDriver(false);
        }
    };

    const canSubmit =
        !!selectedMaker &&
        !!selectedModel &&
        typeof selectedModel.id === 'number' &&
        plate.trim().length > 0;

    const handleSubmit = () => {
        if (!selectedMaker || !selectedModel || !plate.trim()) return;
        onSubmit({
            maker: selectedMaker,
            model: selectedModel,
            plate: normalizePlate(plate),
            drivers: selectedDrivers,
        });
    };

    return (
        <div>
            {title && (
                <h6 className="block text-600 font-medium mb-3">{title}</h6>
            )}
            <div className="row g-3">
                <div className="col-12 col-md-6 vehicle-form-maker-model">
                    <label className="block text-600 text-sm mb-2">Marka*</label>
                    <AutoComplete
                        ref={makerRef}
                        value={makerQ}
                        suggestions={makerS}
                        completeMethod={(e) => {
                            const q = String(e.query || '').toLowerCase();
                            setMakerQ(String(e.query || ''));
                            setMakerS(!q ? makers : makers.filter(m => m.maker_name.toLowerCase().includes(q)));
                        }}
                        field="maker_name"
                        onChange={(e) => {
                            const val = String(e.value || '');
                            setMakerQ(val);
                            if (selectedMaker && val !== selectedMaker.maker_name) {
                                setSelectedMaker(null);
                                setSelectedModel(null);
                                setModelQ('');
                            }
                        }}
                        onSelect={(e) => {
                            const v = e.value as CarMaker;
                            setSelectedMaker(v);
                            setMakerQ(v.maker_name);
                            setSelectedModel(null);
                            setModelQ('');
                        }}
                        onFocus={() => { setMakerS(makers); makerRef.current?.show?.(); }}
                        placeholder="Marka"
                        className="vehicle-form-autocomplete"
                        appendTo="self"
                        dropdown
                    />
                </div>

                <div className="col-12 col-md-6 vehicle-form-maker-model">
                    <label className="block text-600 text-sm mb-2">Model*</label>
                    <AutoComplete
                        ref={modelRef}
                        value={modelQ}
                        suggestions={modelS}
                        completeMethod={(e) => {
                            const list = selectedMaker?.car_models ?? [];
                            const q = String(e.query || '').toLowerCase();
                            setModelQ(String(e.query || ''));
                            setModelS(!q ? list : list.filter(m => m.model_name.toLowerCase().includes(q)));
                        }}
                        field="model_name"
                        onChange={(e) => {
                            const val = String(e.value || '');
                            setModelQ(val);
                            if (selectedModel && val !== selectedModel.model_name) setSelectedModel(null);
                        }}
                        onSelect={(e) => {
                            const v = e.value as CarModel;
                            setSelectedModel(v);
                            setModelQ(v.model_name);
                        }}
                        onFocus={() => {
                            const list = selectedMaker?.car_models ?? [];
                            setModelS(list);
                            modelRef.current?.show?.();
                        }}
                        placeholder="Model"
                        disabled={!selectedMaker}
                        className="vehicle-form-autocomplete"
                        appendTo="self"
                        dropdown
                    />
                </div>

                <div className="col-12 vehicle-form-plate">
                    <label className="block text-600 text-sm mb-2">Numer rejestracyjny*</label>
                    <InputText
                        value={plate}
                        onChange={(e) => {
                            const raw = String(e.target.value || '');
                            setPlate(raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 15));
                        }}
                        maxLength={15}
                        placeholder="Wprowadź numer"
                        className="w-100"
                    />
                </div>

                <div className="col-12">
                    <label className="block text-600 text-sm mb-2">Kierowcy (opcjonalnie)</label>
                    <AutoComplete
                        ref={driversRef}
                        multiple
                        value={selectedDrivers}
                        suggestions={driverS}
                        completeMethod={(e) => {
                            const q = String(e.query || '').toLowerCase();
                            let base = !q
                                ? allDrivers
                                : allDrivers.filter(d =>
                                    (d.phone_number || '').toLowerCase().includes(q) ||
                                    `${d.first_name ?? ''} ${d.last_name ?? ''}`.toLowerCase().includes(q)
                                );
                            setDriverS([{ id: -2, phone_number: 'Wybierz wszystkich' } as any, ...base]);
                        }}
                        selectedItemTemplate={(item) => <span>{item.phone_number}</span>}
                        itemTemplate={(item) => (
                            <div>
                                {item.phone_number}
                                {item.first_name || item.last_name
                                    ? ` • ${item.first_name ?? ''} ${item.last_name ?? ''}`
                                    : ''}
                            </div>
                        )}
                        field="phone_number"
                        onChange={(e) => {
                            let value: FleetDriverListDriver[] = Array.isArray(e.value) ? e.value : [];
                            if (value.some((v: any) => v?.id === -2)) {
                                value = allDrivers.slice();
                            }
                            setSelectedDrivers(value);
                        }}
                        onSelect={() => { driversRef.current?.show?.(); }}
                        onFocus={() => {
                            setDriverS([{ id: -2, phone_number: 'Wybierz wszystkich' } as any, ...allDrivers]);
                            driversRef.current?.show?.();
                        }}
                        onShow={() => {
                            setDriverS([{ id: -2, phone_number: 'Wybierz wszystkich' } as any, ...allDrivers]);
                        }}
                        placeholder="Kierowca"
                        className="vehicle-form-autocomplete"
                        appendTo="self"
                        dropdown
                        panelStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
                        panelFooterTemplate={() => (
                            <div className="vehicle-form-new-driver p-3" onClick={(e) => e.stopPropagation()}>
                                <NewDriverMiniForm
                                    phone={newDriverPhone}
                                    name={newDriverName}
                                    onPhoneChange={setNewDriverPhone}
                                    onNameChange={setNewDriverName}
                                    onAdd={handleCreateDriver}
                                    disabled={creatingDriver}
                                    loading={creatingDriver}
                                    error={createDriverError}
                                />
                            </div>
                        )}
                    />
                </div>

                <div className="col-12 d-flex justify-content-end gap-2">
                    {onCancel && (
                        <Button
                            label="Anuluj"
                            className="p-button-outlined p-button-secondary"
                            onClick={onCancel}
                            disabled={saving}
                        />
                    )}
                    <Button
                        className='accept-adding-car'
                        label={submitLabel}
                        onClick={handleSubmit}
                        disabled={!canSubmit || saving}
                        loading={saving}
                    />
                </div>
            </div>
        </div>
    );
};

export function DriverChips({ drivers }: { drivers: FleetDriverListDriver[] }) {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [visibleCount, setVisibleCount] = React.useState<number>(drivers.length);

    React.useEffect(() => {
        setVisibleCount(drivers.length);
    }, [drivers]);

    React.useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        let frame: number | null = null;
        let resizing = false;

        const check = () => {
            if (!el || resizing) return;
            resizing = true;
            frame = requestAnimationFrame(() => {
                const fits = el.scrollWidth <= el.clientWidth;
                if (!fits && visibleCount > 0) {
                    setVisibleCount((c) => Math.max(0, c - 1));
                } else if (fits && visibleCount < drivers.length) {
                    setVisibleCount(visibleCount + 1);
                }
                resizing = false;
            });
        };

        const ro = new ResizeObserver(() => check());
        ro.observe(el);
        check();

        return () => {
            ro.disconnect();
            if (frame) cancelAnimationFrame(frame);
        };
    }, [visibleCount, drivers.length]);

    React.useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        if (el.scrollWidth > el.clientWidth && visibleCount > 0) {
            setVisibleCount((c) => Math.max(0, c - 1));
        }
    }, [visibleCount]);

    return (
        <div className="d-flex gap-2 phone-drivers-line" ref={containerRef}>
            <img
                alt="driver-configure"
                style={{ marginRight: '3px', marginTop: '2px' }}
                src="/fleet/car/configure-driver.svg"
            />
            {drivers.slice(0, visibleCount).map((d) => (
                <span key={d.id} className="vehicle-chip small">
                    {d.phone_number}
                </span>
            ))}
            {drivers.length > visibleCount && (
                <span className="text-secondary small">+{drivers.length - visibleCount} więcej</span>
            )}
        </div>
    );
}

interface CarListItemProps {
    index: number;
    makerName: string;
    modelName: string;
    plate: string;
    drivers?: FleetDriverListDriver[];
    onEdit: () => void;
    onDelete: () => void;
}

export function CarListItem({ index, makerName, modelName, plate, drivers, onEdit, onDelete }: CarListItemProps) {
    return (
        <div className="car-list-item">
            <div className="row align-items-center">
                <div className="col-10">
                    <div className="row">
                        <div className="col-12 d-flex align-items-center mb-1 gap-2">
                            <span className="car-list-item__badge d-none d-md-inline-flex">
                                <img
                                    alt="car-configure"
                                    style={{ height: '14px', marginBottom: '1px' }}
                                    src="/fleet/car/configure-car.svg"
                                />
                                Pojazd {index}
                            </span>
                            <span className="car-list-item__label">
                                {makerName} {modelName} | {plate}
                            </span>
                        </div>
                        {drivers && drivers.length > 0 && (
                            <div className="col-12 mt-1">
                                <DriverChips drivers={drivers} />
                            </div>
                        )}
                    </div>
                </div>
                <div className="col-2 d-flex justify-content-end align-self-center">
                    <div className="d-flex gap-1">
                        <Button
                            icon={<img src="/fleet/car/configuration-edit.svg" alt="edit" style={{ height: '18px' }} />}
                            className="p-button-text car-list-item__btn"
                            onClick={onEdit}
                        />
                        <Button
                            icon={<img src="/fleet/car/configuration-remove.svg" alt="remove" style={{ height: '18px' }} />}
                            className="p-button-text p-button-danger car-list-item__btn"
                            onClick={onDelete}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

interface EditCarFormProps {
    makers: CarMaker[];
    allDrivers: FleetDriverListDriver[];
    initial?: VehicleInitial;
    saving?: boolean;
    onSubmit: (data: VehicleFormData) => void;
    onCancel: () => void;
    onDriverCreated?: (driver: FleetDriverListDriver) => void;
    submitLabel?: string;
}

export function EditCarForm({ makers, allDrivers, initial, saving, onSubmit, onCancel, onDriverCreated, submitLabel = 'Zapisz' }: EditCarFormProps) {
    return (
        <div className="vehicle-edit-form">
            <div className="col-12 d-flex align-items-center mb-3">
                <span className="car-list-item__badge">
                    <img
                        alt="car-configure"
                        style={{ height: '14px', marginBottom: '1px' }}
                        src="/fleet/car/configure-car.svg"
                    />
                    Edycja pojazdu
                </span>
            </div>
            <VehicleForm
                makers={makers}
                allDrivers={allDrivers}
                initial={initial}
                onSubmit={onSubmit}
                onCancel={onCancel}
                onDriverCreated={onDriverCreated}
                submitLabel={submitLabel}
                saving={saving}
            />
        </div>
    );
}
